import { useClerk, useUser } from '@clerk/react';
import {
  Binoculars,
  CalendarBlank,
  ChartBar,
  FilmStrip,
  FlowArrow,
  Gear,
  House,
  SignOut,
  Users,
  UsersThree,
} from '@phosphor-icons/react';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  Button,
  MenuDivider,
  MenuHeader,
  MenuItem,
  MenuRadioGroup,
  MenuRadioItem,
  NavGroup,
  NavItem,
  NavSubItem,
  Sidebar,
  SidebarContent,
  SidebarDivider,
  SidebarFooter,
  SidebarHeader,
  SidebarSection,
  SidebarUser,
  Well,
  WorkspaceSwitcher,
} from '@reelplicate/ui';
import { type ComponentProps, useState } from 'react';
import { Link as RouterLink, Outlet, useLocation } from 'react-router';

import { useRunningCount } from '@/features/flows/running/use-running-count';
import { NotificationsNavItem } from '@/features/notifications/notifications-nav-item';
import { useMediaQuery } from '@/lib/use-media-query';

import { AppCommandPalette } from './app-command-palette';
import { FullBleedContext } from './layout-mode';

export type NavLinkItem = {
  to: string;
  label: string;
  icon: ComponentProps<typeof NavItem>['icon'];
};

const WORKSPACE_NAV: NavLinkItem[] = [
  { to: '/', label: 'Home', icon: House },
  { to: '/media', label: 'Media', icon: FilmStrip },
  { to: '/competitors', label: 'Competitors', icon: Binoculars },
  { to: '/personas', label: 'Personas', icon: UsersThree },
  { to: '/calendar', label: 'Calendar', icon: CalendarBlank },
  { to: '/analytics', label: 'Analytics', icon: ChartBar },
];

const FLOWS_NAV = [
  { to: '/flows', label: 'All flows' },
  { to: '/flows/running', label: 'Running' },
  { to: '/flows/drafts', label: 'Drafts' },
  { to: '/flows/scheduled', label: 'Scheduled' },
];

const SETTINGS_NAV: NavLinkItem[] = [
  { to: '/team', label: 'Team', icon: Users },
  { to: '/settings', label: 'Settings', icon: Gear },
];

/** Every sidebar destination, for the command palette's "Go to". */
const PAGES: NavLinkItem[] = [
  ...WORKSPACE_NAV,
  ...FLOWS_NAV.map((page) => ({ ...page, icon: FlowArrow })),
  ...SETTINGS_NAV,
];

/** A nav destination is current on its own page and on the pages under it. */
function isCurrent(pathname: string, to: string) {
  return pathname === to || (to !== '/' && pathname.startsWith(`${to}/`));
}

/**
 * The signed-in app shell (Figma "Screen — Home", 140:531): the Sidebar
 * (61:1000) beside a scrolling main area on bg/canvas. The sidebar collapses
 * to its icon rail below 1280, as the Figma component spec says.
 */
export function AppLayout() {
  const wide = useMediaQuery('(min-width: 1280px)');
  const [fullBleed, setFullBleed] = useState(false);
  const collapsed = fullBleed || !wide;
  const { pathname } = useLocation();
  const running = useRunningCount();

  const navItem = ({ to, label, icon }: NavLinkItem) => (
    <NavItem key={to} asChild icon={icon} active={isCurrent(pathname, to)}>
      <RouterLink to={to}>{label}</RouterLink>
    </NavItem>
  );

  return (
    <div className="bg-bg-canvas flex h-svh">
      <Sidebar collapsed={collapsed}>
        <SidebarHeader>
          <CurrentWorkspace />
        </SidebarHeader>
        <SidebarContent>
          <SidebarSection>
            <NotificationsNavItem />
          </SidebarSection>
          <SidebarSection label="Workspace">
            {WORKSPACE_NAV.map(navItem)}
            <NavGroup
              icon={FlowArrow}
              label="Flows"
              defaultOpen
              active={pathname === '/flows' || pathname.startsWith('/flows/')}
            >
              {FLOWS_NAV.map(({ to, label }) => (
                <NavSubItem
                  key={to}
                  asChild
                  active={pathname === to}
                  count={to === '/flows/running' && running > 0 ? running : undefined}
                >
                  <RouterLink to={to}>{label}</RouterLink>
                </NavSubItem>
              ))}
            </NavGroup>
          </SidebarSection>
          <SidebarSection label="Settings">{SETTINGS_NAV.map(navItem)}</SidebarSection>
        </SidebarContent>
        <SidebarFooter>
          {!collapsed && (
            <Well
              tone="brand"
              title="Upgrade to Scale"
              action={
                <Button variant="outline" size="sm">
                  Learn more
                </Button>
              }
            >
              10 seats, priority rendering and 500 reels a month.
            </Well>
          )}
          <SidebarDivider />
          <CurrentUser />
        </SidebarFooter>
      </Sidebar>
      {/* relative: absolute descendants (sr-only text too) scroll inside main
          instead of stretching the page past the viewport. */}
      <main
        className={
          fullBleed
            ? 'relative min-w-0 flex-1 overflow-hidden'
            : 'relative min-w-0 flex-1 overflow-y-auto'
        }
      >
        <FullBleedContext value={setFullBleed}>
          <Outlet />
        </FullBleedContext>
      </main>
      <AppCommandPalette pages={PAGES} />
    </div>
  );
}

/** Workspace Switcher for the user's workspace (one per user for now). */
function CurrentWorkspace() {
  const { data: workspaces } = useListWorkspaces();
  const workspace = workspaces?.[0];
  return (
    <WorkspaceSwitcher
      name={workspace?.name ?? 'Workspace'}
      plan="Growth · 3 members"
      logo={
        workspace?.logoUrl ? (
          <img
            src={workspace.logoUrl}
            alt=""
            width={32}
            height={32}
            className="size-8 shrink-0 rounded-sm object-cover"
          />
        ) : undefined
      }
    >
      <MenuRadioGroup value={workspace?.id}>
        {workspaces?.map((w) => (
          <MenuRadioItem key={w.id} value={w.id}>
            {w.name}
          </MenuRadioItem>
        ))}
      </MenuRadioGroup>
    </WorkspaceSwitcher>
  );
}

/** Sidebar User for the signed-in Clerk user, with sign out in its menu. */
function CurrentUser() {
  const { user } = useUser();
  const { signOut } = useClerk();
  if (!user) return null;

  const email = user.primaryEmailAddress?.emailAddress;
  const name = user.fullName ?? email ?? 'Account';

  return (
    <SidebarUser name={name} email={email} src={user.imageUrl}>
      <MenuHeader>{email ?? name}</MenuHeader>
      <MenuDivider />
      <MenuItem
        leadingIcon={SignOut}
        onSelect={() => void signOut({ redirectUrl: '/sign-in' })}
      >
        Sign out
      </MenuItem>
    </SidebarUser>
  );
}
